import { z } from "zod";

/**
 * Shared DTO cho Admin AI multi-provider (US-AE-IMP.F6-0118 / @UC9148):
 *  - provider của model (anthropic|google|openai) — dùng chung với model-config (admin-ai-eval).
 *  - provider-credential set (input apiKey — task 0119 UI) vs status (output an toàn, KHÔNG BAO GIỜ key).
 *
 * Một nguồn sự thật shape cho api (S05 router) + admin web (task 0119). Đồng bộ enum provider với
 * db (S01 cột provider) + domain (S03 provider-registry).
 *
 * BẢO MẬT / BẤT BIẾN (invariants — G4):
 * - `SetProviderCredentialRequest.apiKey` là INPUT-ONLY: KHÔNG rò rỉ sang bất kỳ response type nào.
 * - `ProviderCredentialStatus` TUYỆT ĐỐI KHÔNG chứa `apiKey`/`encryptedKey`/key thô — chỉ
 *   provider + configured + keyLast4 (4 ký tự cuối, hiển thị) + rotatedAt.
 * - `.strict()` — reject key lạ tại biên (chống leak/inject).
 * - provider = khoá cứng 3 giá trị (provider MỚI = task riêng thêm adapter, index §6c).
 */

/** Nhà cung cấp LLM có adapter runtime. Khoá cứng 3 (provider mới ⇒ task riêng, không string tự do). */
export const llmProviderSchema = z.enum(["anthropic", "google", "openai"]);
export type LlmProviderContract = z.infer<typeof llmProviderSchema>;

/** Danh sách provider hợp lệ (đồng bộ với domain SUPPORTED_PROVIDERS). */
export const LLM_PROVIDERS = llmProviderSchema.options;

/** Giới hạn độ dài apiKey thô (chống payload lạ). Key thật các provider ~40-200 ký tự. */
export const PROVIDER_API_KEY_MAX_LEN = 500;

/**
 * POST /admin/ai/providers/:provider/key (task 0119 UI) — set/rotate key nhà cung cấp.
 * `apiKey` INPUT-ONLY: server mã hoá at-rest (S05), KHÔNG trả lại. `.strict()` chặn key lạ.
 */
export const setProviderCredentialRequestSchema = z
  .object({
    provider: llmProviderSchema,
    apiKey: z.string().min(1).max(PROVIDER_API_KEY_MAX_LEN),
  })
  .strict();
export type SetProviderCredentialRequest = z.infer<typeof setProviderCredentialRequestSchema>;

/**
 * Trạng thái credential một provider (output AN TOÀN — KHÔNG key). `configured=false` ⇒ keyLast4=null.
 * keyLast4 = 4 ký tự cuối key thô (chỉ hiển thị "••••abcd") — KHÔNG đủ suy ra key.
 */
export const providerCredentialStatusSchema = z
  .object({
    provider: llmProviderSchema,
    configured: z.boolean(),
    keyLast4: z.string().length(4).nullable(),
    rotatedAt: z.string().nullable(),
  })
  .strict();
export type ProviderCredentialStatus = z.infer<typeof providerCredentialStatusSchema>;

/** GET /admin/ai/providers — trạng thái 3 provider (configured true/false). */
export const listProviderCredentialStatusResponseSchema = z
  .object({
    providers: z.array(providerCredentialStatusSchema),
  })
  .strict();
export type ListProviderCredentialStatusResponse = z.infer<
  typeof listProviderCredentialStatusResponseSchema
>;
